// The offer to resolve a defect when the remedial job raised from it is approved (Session ARK, gap
// W-10 in docs/reports/OFFICE-UI-RESTRUCTURE-AUDIT-2026-09-23.md).
//
// Shown in the Service Record panel beside the other notes the approval returns (the draft email,
// the invoice and the timesheet notes). It is an OFFER and nothing more: a finished repair does not by
// itself prove the fault is fixed, so the office decides, defect by defect, and leaving a defect open
// is a perfectly good answer. "Mark resolved" is the SAME call the defect page's own button makes
// (api.defects.resolve, PATCH /api/defects/:id), so it writes and audits exactly what a manual resolve
// does. "Not now" only hides this box: the defect stays open, stays on the Defects list, and its own
// page keeps offering the same choice for as long as it is open.
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import type { RemedialDefectOffer as Offer } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";

type RowState = { busy: boolean; resolved: boolean; error: string | null };
const IDLE: RowState = { busy: false, resolved: false, error: null };

export function RemedialDefectOffer({ defects }: { defects: Offer[] }) {
  const [dismissed, setDismissed] = useState(false);
  const [rows, setRows] = useState<Record<string, RowState>>({});

  if (dismissed || defects.length === 0) return null;

  const stateOf = (id: string): RowState => rows[id] ?? IDLE;
  const setRow = (id: string, next: Partial<RowState>) => setRows((prev) => ({ ...prev, [id]: { ...(prev[id] ?? IDLE), ...next } }));

  async function resolve(id: string) {
    if (stateOf(id).busy || stateOf(id).resolved) return;
    setRow(id, { busy: true, error: null });
    try {
      await api.defects.resolve(id, "resolved");
      setRow(id, { busy: false, resolved: true });
    } catch (err) {
      setRow(id, { busy: false, error: friendlyApiError(err, "Could not mark this defect resolved. Please try again.") });
    }
  }

  const single = defects.length === 1;
  return (
    <section aria-label="Defects this job was raised to fix" className="mt-1 w-full rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
      <p className="text-sm text-ele-strong">
        {single ? "This job was raised to fix a defect." : `This job was raised to fix ${defects.length} defects.`}
      </p>
      <p className="mb-2 text-xs text-ele-body">
        {single
          ? "If the work fixed it, mark it resolved. If not, leave it open."
          : "Mark each one resolved if the work fixed it. Leave any that are not fixed open."}
      </p>
      <ul className="space-y-2">
        {defects.map((d) => {
          const st = stateOf(d.id);
          return (
            <li key={d.id} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="min-w-0 flex-1 text-ele-body">
                {d.description}
                <span className="ml-2 font-menu text-xs uppercase tracking-wide opacity-70">{d.severity === "immediate" ? "Immediate" : "Advisory"}</span>
              </span>
              <Link to={`/office/defects/${encodeURIComponent(d.id)}`} className="font-menu text-xs tracking-wide text-ele-green hover:underline">
                Open defect
              </Link>
              {st.resolved ? (
                <span role="status" className="font-menu text-xs tracking-wide text-ele-green">
                  Resolved
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => void resolve(d.id)}
                  disabled={st.busy}
                  className="max-sm:min-h-11 rounded-ele border border-ele-blue px-3 py-1 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-inset disabled:opacity-50"
                >
                  {st.busy ? "Saving..." : "Mark resolved"}
                </button>
              )}
              {st.error && (
                <span role="alert" className="w-full text-xs text-ele-danger">
                  {st.error}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="mt-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
      >
        Not now
      </button>
    </section>
  );
}
